import { Loader2, Paperclip } from "lucide-react";
import { useId, useRef, useState, type ReactNode } from "react";

export interface FileDropzoneProps {
  readonly label: string;
  readonly accept: string;
  readonly maxSize: number;
  readonly loading?: boolean;
  readonly disabled?: boolean;
  readonly onFileSelected: (file: File) => void;
}

export function FileDropzone({
  label,
  accept,
  maxSize,
  loading,
  disabled,
  onFileSelected,
}: FileDropzoneProps): ReactNode {
  const [error, setError] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File>();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const select = (file: File): void => {
    if (loading || disabled) return;
    const allowed = accept
      .split(",")
      .some((type) =>
        type.trim().startsWith(".")
          ? file.name.toLowerCase().endsWith(type.trim().toLowerCase())
          : type.trim().endsWith("/*")
            ? file.type.startsWith(type.trim().slice(0, -1))
            : file.type === type.trim(),
      );
    if (!allowed || file.size > maxSize) {
      setError(
        !allowed
          ? "Dit bestandstype wordt niet geaccepteerd."
          : "Het bestand is te groot.",
      );
      return;
    }
    setError(undefined);
    setSelectedFile(file);
    onFileSelected(file);
  };
  return (
    <section
      aria-label={label}
      className="rounded-xl border border-dashed border-border bg-muted/25 p-5"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) select(file);
      }}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        aria-label={label}
        accept={accept}
        disabled={loading || disabled}
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) select(file);
          event.target.value = "";
        }}
      />
      <button
        type="button"
        disabled={loading || disabled}
        onClick={() => inputRef.current?.click()}
        aria-label={label}
        aria-describedby={`${inputId}-hint`}
        className="flex w-full flex-col items-center gap-2 rounded-lg p-4 text-center text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      >
        {loading ? (
          <Loader2 className="size-6 animate-spin" aria-hidden="true" />
        ) : (
          <Paperclip className="size-6" aria-hidden="true" />
        )}
        <span className="font-medium">{label}</span>
        <span id={`${inputId}-hint`} className="text-muted-foreground">
          Sleep een bestand hierheen of kies een bestand. Maximaal{" "}
          {Math.round(maxSize / 1024 / 1024)} MB.
        </span>
      </button>
      {(selectedFile || loading) && (
        <p
          role="status"
          className="mt-2 break-all text-center text-sm text-muted-foreground"
        >
          {selectedFile &&
            `${selectedFile.name} (${Math.ceil(selectedFile.size / 1024)} kB)`}
          {loading && " — Bestand uploaden…"}
        </p>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-destructive">
          {error}
        </p>
      )}
    </section>
  );
}
